import { TRefElementInstance } from 'widgets/toolbox/RefElement';
import { get } from 'widgets/toolbox/util';
import { scrollWindowTo } from 'widgets/toolbox/scroll';
import { getContentByUrl } from 'widgets/toolbox/ajax';
import { appendParamToURL } from 'widgets/toolbox/util';
import { unwrapElementsByClasses } from 'widgets/toolbox/dom';

import { TAccordionInstance } from 'widgets/global/Accordion';
import { TModalInstance } from 'widgets/global/Modal';
import { TInputSelectInstance } from 'widgets/forms/InputSelect';

type THistory = {
    showAjaxUrl: string;
    showUrl: string;
};

/**
 * @param Tabs Base widget for extending
 * @returns ProductListingMgr widget
 */
function ProductListingMgrClassCreator(Tabs: import('widgets/global/Tabs').TTabs) {
    /**
     * @category widgets
     * @subcategory search
     * @class ProductListingMgr
     * @augments Tabs
     * @classdesc Represents ProductListingMgr component with next features:
     * 1. Updating product grid using product refinement, sorting and paging
     * 2. Load previous/next product tile
     * 3. Allow handling no search result case
     * 4. Allow handling "Enter" keydown event to be able to skip refinements and set focus to the first element of the grid
     * @property {string} data-show-url Search show url
     * @property {string} data-show-ajax-url Search show AJAX url
     * @property {string} data-load-more-block Block with a button `Load more` for products
     * @property {string} data-load-more-prev Block with a button `Load previous` for products
     * @property {string} data-product-progress-indicator - reference element with search progress indicator for products search
     * @property {string} data-content-progress-indicator - reference element with search progress indicator for content search
     * @property {string} data-load-more-content-block - Block with a button `Load more` for content
     * @property {object} data-accessibility-alerts - Accessibility alerts messages for different user actions
     * @property {boolean} data-first-page - First page flag
     * Possible values are: `filtersapplied`, `filterremoved`, `sortingapplied`, `productlistupdated`, `addedtowishlist`, `previousstatereverted`

     * @example <caption>Example of ProductListingMgr widget usage</caption>
     * <div
     *     data-widget="productListingMgr"
     *     data-show-url="${pdict.productSearch.historyUrl}"
     *     data-show-ajax-url="${pdict.productSearch.ajaxUrl}"
     *     data-category="${pdict.productSearch.isCategorySearch ? pdict.productSearch.category.id : null}"
     *     data-auto-activation="true"
     *     data-use-url-anchor="true"
     *     data-accessibility-alerts='{
     *         "filtersapplied": "${Resource.msg('alert.filtersapplied', 'search', null)}",
     *         "filterremoved": "${Resource.msg('alert.filterremoved', 'search', null)}",
     *         "sortingapplied": "${Resource.msg('alert.sortingapplied', 'search', null)}",
     *         "productlistupdated": "${Resource.msg('alert.productlistupdated', 'search', null)}",
     *         "contentlistupdated": "${Resource.msg('alert.contentlistupdated', 'search', null)}",
     *         "addedtowishlist": "${Resource.msg('alert.addedtowishlist', 'product', null)}",
     *         "removedfromwishlist": "${Resource.msg('alert.removedfromwishlist', 'product', null)}",
     *         "previousstatereverted": "${Resource.msg('alert.previousstatereverted', 'search', null)}"
     *     }'
     *     data-first-page="${pdict.productSearch.firstPage}"
     *     ...
     * >
     *     ... product list content
     * </div>
     */
    class ProductListingMgr extends Tabs {
        prefs() {
            return {
                classesActiveSection: 'm-expanded',
                classesBusy: 'm-busy',
                loadMoreBlock: 'loadMoreBlock',
                loadPrevBlock: 'loadPrevBlock',
                loadMoreContentBlock: 'loadMoreContentBlock',
                accessibilityAlerts: <TAccessibilityAlerts>{},
                showAjaxUrl: '',
                showUrl: '',
                firstPage: true,
                ...super.prefs()
            };
        }

        init() {
            super.init();
            this.initEvents();

            if (!this.prefs().firstPage) {
                scrollWindowTo(this.ref('grid').get(), true);
            }
        }

        /**
         * @description Update product grid by url
         * @param url Update URL
         * @param ref Reference element
         * @param handleHistory Handle history flag
         * @returns Promise object represents server response with products list markup
         */
        updateByUrl(url: string, ref?: TRefElementInstance, handleHistory?: boolean): Promise<string | void | null> {
            this.busy();

            return getContentByUrl(url)
                .then(response => {
                    if (response && typeof response === 'string') {
                        if (this.isNoResultsSearch(response) && !handleHistory) {
                            return Promise.reject();
                        }

                        return this.updateContent(response, ref, handleHistory);
                    }

                    return response;
                }).catch(() => {
                    if (!/\?q=/g.test(url)) {
                        // By this we filter refinements requests and search requests.
                        // Search request fired when page returned from Back-Forward Cache.
                        // We should show "No refinement combination" dialog only in case of refinement requests.
                        this.handleNoResult();
                    }

                    return Promise.resolve(null);
                }).finally(() => {
                    this.unbusy();
                });
        }

        /**
         * @description Checks if received response string contains comment marker which means that markup includes no result page
         * @param response Response string
         */
        isNoResultsSearch(response: string): boolean {
            return response.indexOf('conditions evaluated to no search results') !== -1;
        }

        /**
         * @description Render response string with product list content updates to the Reference Element
         * @param response Response string
         * @param ref Reference Element
         * @param handleHistory Handle history flag
         */
        updateContent(response: string, ref?: TRefElementInstance, handleHistory?: boolean): Promise<string|null> {
            return this.render(undefined, undefined, ref || this.ref('self'), response)
                .then(() => {
                    if (handleHistory) {
                        this.handleHistory();
                        scrollWindowTo(this.ref('grid').get(), true);
                    }
                })
                .then(() => response);
        }

        /**
         * @description Handle History
         * @param isReplace Replace flag
         * @param [history] history object
         */
        handleHistory(isReplace = false, history?: THistory) {
            const state = {
                ajaxUrl: history ? history.showAjaxUrl : this.prefs().showAjaxUrl
            };
            let historyURL = history ? history.showUrl : this.prefs().showUrl;

            if (document.location.hash) {
                historyURL += document.location.hash;
            }

            historyURL = historyURL.replace(/%20/g, '+');

            if (isReplace) {
                window.history.replaceState(state, '', historyURL);
            } else {
                window.history.pushState(state, '', historyURL);
            }
        }

        /**
         * @description Update View
         * @listens dom#click
         * @param button Target element
         */
        updateView(button: TRefElementInstance) {
            const updateURL = String(button.data('href'));
            const refinementSelected = button.data('checked');
            const alertMessage = refinementSelected
                ? this.prefs().accessibilityAlerts.filterremoved
                : this.prefs().accessibilityAlerts.filtersapplied;

            return this.updateByUrl(updateURL, undefined, true)
                .then(() => {
                    this.accessibilityAlert(alertMessage);

                    this.getByIdDeep<TAccordionInstance, void>('refinementsAccordion', refinementsAccordion => {
                        refinementsAccordion.reinit();
                    });
                });
        }

        /**
         * @description Update Grid
         * @listens dom#change
         * @param select Target element
         */
        updateGrid(select: TInputSelectInstance) {
            const selectedSorting = select.getSelectedOptions();
            let updateByUrlPromise;

            if (selectedSorting) {
                const url = String(selectedSorting.data('url'));

                updateByUrlPromise = this.updateByUrl(url, undefined, true)
                    .then(() => {
                        select.ref('field').focus();
                        this.accessibilityAlert(this.prefs().accessibilityAlerts.sortingapplied);
                    });
            }

            return updateByUrlPromise || Promise.resolve(null);
        }

        /**
         * @description Load more products
         * @listens dom#click
         * @param button Target element
         */
        loadMore(button: TRefElementInstance) {
            const href = button.data('show-url');
            const history = {
                showUrl: button.attr('href'),
                showAjaxUrl: button.data('show-ajax-url')
            };
            let url = '';

            if (typeof href === 'string') {
                url = href;
            }

            const lastIndex = this.getProductTiles().length;

            this.ref('loadMoreBlock').remove();

            return this.loadChunk(url, response => {
                if (typeof response === 'string') {
                    const gridElements = this.parseProductGridUpdateResponse(response);

                    this.ref('productGrid').append(gridElements);
                    this.accessibilityAlert(this.prefs().accessibilityAlerts.productlistupdated);
                    this.focusProductTilesByIndex(lastIndex);
                }
            }, true, history);
        }

        /**
         * @description Load previous products
         * @listens dom#click
         * @param button Target element
         */
        loadPrev(button: TRefElementInstance) {
            const href = button.data('show-url');
            const history = {
                showUrl: button.attr('href'),
                showAjaxUrl: button.data('show-ajax-url')
            };
            let url = '';

            if (typeof href === 'string') {
                url = href;
            }

            const lastIndex = this.getProductTiles().length;

            this.ref('loadPrevBlock').remove();

            return this.loadChunk(url, response => {
                if (typeof response === 'string') {
                    const gridElements = this.parseProductGridUpdateResponse(response);

                    this.ref('productGrid').prepend(gridElements);
                    this.accessibilityAlert(this.prefs().accessibilityAlerts.productlistupdated);
                    this.focusProductTilesByIndex(this.getProductTiles().length - (lastIndex + 1));
                }
            }, true, history);
        }

        /**
         * @description Removes Page Designer region wrappers
         * @param response processed string
         */
        parseProductGridUpdateResponse(response: string) {
            const gridElements = unwrapElementsByClasses(response, 'experience-main', 'experience-dynamic-productList');

            return gridElements;
        }

        /**
         * @description Load more content
         * @listens dom#click
         * @param button Target element
         */
        loadMoreContent(button: TRefElementInstance) {
            const href = button.attr('href');
            let url = '';

            if (typeof href === 'string') {
                url = href;
            }

            this.ref('loadMoreContentBlock').remove();

            this.loadChunk(url, response => {
                if (typeof response === 'string') {
                    this.ref('contentGrid').append(response);
                    this.accessibilityAlert(this.prefs().accessibilityAlerts.contentlistupdated);
                }
            }, false);
        }

        /**
         * @description Loads chunk of data and executes callback. Data might be either products or content
         * @param url load chunk url
         * @param successCb success callback to be called after chunk was loaded with response as an argument
         * @param handleHistory do we need to handle history change after chunk loaded
         * @param [history] history object
         */
        loadChunk(url: string, successCb: (arg: string) => void, handleHistory: boolean, history?) {
            let getContentByUrlPromise;

            if (!url) {
                // eslint-disable-next-line no-console
                console.error('Something wrong with target URL');
            } else {
                this.busy();

                getContentByUrlPromise = getContentByUrl(url).then(response => {
                    successCb(response);

                    if (handleHistory) {
                        this.handleHistory(true, history);
                    }
                }).finally(() => {
                    this.unbusy();
                });
            }

            return getContentByUrlPromise || Promise.resolve(null);
        }

        /**
         * @description Show appropriate Global Alert for page changes
         * @emits "alert.show"
         * @param message - alert message
         */
        accessibilityAlert(message: string) {
            // We need to show alerts with delay to not overlap with other big changes on the page
            /**
             * @description Global event to show alert
             * @event "alert.show"
             */
            setTimeout(() => this.eventBus().emit('alert.show', { accessibilityAlert: message }), 400);
        }

        /**
         * @description Get Product tiles HTMLElements list
         */
        getProductTiles(): Array<HTMLElement> {
            let tiles = [];
            const productGridElement = this.ref('productGrid').get();

            if (productGridElement) {
                tiles = Array.from(productGridElement.querySelectorAll('[data-widget=productTile][data-grid-item=true]'));
            }

            return tiles;
        }

        /**
         * @description Focus Product tiles element by provided index
         * @param index - index of product tiles list
         */
        focusProductTilesByIndex(index = 0) {
            const tiles = this.getProductTiles();

            tiles[index]?.focus();
        }

        /**
         * @description Mark product grid as busy element
         */
        busy() {
            this.ref('productGrid').addClass(this.prefs().classesBusy).attr('aria-busy', 'true');
            this.ref('contentGrid').addClass(this.prefs().classesBusy).attr('aria-busy', 'true');
        }

        /**
         * @description Mark product grid as ready for interaction element
         */
        unbusy() {
            this.ref('productGrid').removeClass(this.prefs().classesBusy).attr('aria-busy', false);
            this.ref('contentGrid').removeClass(this.prefs().classesBusy).attr('aria-busy', false);
        }

        /**
         * @description Init Events
         * @emits "alert.show"
         */
        initEvents() {
            this.ev('popstate', (_target, event) => {
                if (event instanceof PopStateEvent && event.state) {
                    const hashChangedOnly = get(event, 'state.hashChangedOnly', false);

                    if (hashChangedOnly) {
                        this.handleUrlChange(false);

                        return;
                    }

                    let updateUrl = get(event, 'state.ajaxUrl', window.location.href);

                    updateUrl = appendParamToURL(updateUrl, 'history', 'true');

                    this.updateByUrl(updateUrl, undefined, true)
                        .then(() => {
                            this.handleUrlChange(false);
                            const accessibilityAlert = this.prefs().accessibilityAlerts.previousstatereverted;

                            /**
                             * @description Global event to show alert
                             * @event "alert.show"
                             */
                            this.eventBus().emit('alert.show', {
                                accessibilityAlert
                            });
                        });
                }
            }, window);
        }

        /**
         * @description No Result Combination handler
         */
        handleNoResult() {
            this.getById('noResultPopup', (popup: TModalInstance) => popup.showModal());
        }
    }

    return ProductListingMgr;
}

export type TProductListingMgr = ReturnType<typeof ProductListingMgrClassCreator>;

export type TProductListingMgrInstance = InstanceType<TProductListingMgr>;

export default ProductListingMgrClassCreator;
